import {
  Alert,
  Divider,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useSearchParams } from "react-router";

import { useDomainServices } from "@/providers/DomainServices";
import { type AgentFeedbackRecord } from "@/shared/domain/agentThread";

import { AdminAgentThreadViewer } from "./AdminAgentThreadViewer";
import { AgentThreadsTable } from "./AgentThreadsTable";
import styles from "./ExploreAgentThreads.module.css";
import {
  nextExploreUrlParams,
  shouldHydrateFromUrl,
  type ViewMode,
  viewModeFromUrl,
} from "./exploreUrlSync";
import { FeedbackSentimentIcon } from "./FeedbackSentimentIcon";
import { FeedbackTable } from "./FeedbackTable";
import { FeedbackTriagePanel } from "./FeedbackTriagePanel";
import {
  buildAgentOptionResolver,
  buildOrgOptionResolver,
  buildUserOptionResolver,
} from "./filterOptionResolvers";
import {
  buildAdminAgentThreadLink,
  buildAdminFeedbackLink,
} from "./shareLinks";

export function ExploreAgentThreads() {
  // Single hook call — used for both ``orgService`` (filter dropdowns) and
  // ``adminService`` (deep-link hydration + filter dropdowns). Calling
  // ``useDomainServices`` twice in the same component is fine but easy to
  // drift out of sync.
  const { orgService, adminService } = useDomainServices();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // View mode is driven by React state rather than searchParams because
  // direct ``setViewMode`` calls and the deep-link sync effect used to race
  // over the same URL slot. A single effect below reflects state → URL.
  const [viewMode, setViewMode] = React.useState<ViewMode>(() =>
    viewModeFromUrl(searchParams),
  );

  // Lazy, server-searched option resolvers for the Org / User / Agent filter
  // dropdowns. Nothing is fetched at mount: the ResourceView filter widget
  // invokes a resolver only when its value picker opens, then again (debounced)
  // as the admin types, capping results server-side at 100. Stable across
  // renders because the service instances are provider-memoized and
  // `useQueryClient` returns the same client instance every render, so the
  // filter definitions stay referentially quiet. (The widget's option cache
  // is keyed independently of resolver identity, so it doesn't depend on
  // this memoization. The resolvers themselves also retain results in the
  // query cache — see `buildSearchOptionsResolver` — so a picker reopen after
  // remount serves the last page instantly.)
  const orgOptions = React.useMemo(
    () => buildOrgOptionResolver(queryClient, orgService),
    [queryClient, orgService],
  );
  const userOptions = React.useMemo(
    () => buildUserOptionResolver(queryClient, adminService),
    [queryClient, adminService],
  );
  const agentOptions = React.useMemo(
    () => buildAgentOptionResolver(queryClient, adminService),
    [queryClient, adminService],
  );

  const [selectedAgentThreadId, setSelectedAgentThreadId] = React.useState<
    string | undefined
  >();
  const [scrollToMessageSequenceNum, setScrollToMessageSequenceNum] =
    React.useState<number | undefined>();
  const [selectedFeedback, setSelectedFeedback] = React.useState<
    AgentFeedbackRecord | undefined
  >();

  // ---- Feedback view state ----
  // Seeds the feedback table's Session filter on the next feedback-view mount
  // (deep links + the threads table's "show feedback for this thread" hand-off).
  // The table owns its own filters/pagination otherwise; this is just the
  // initial scoping value. Cleared on a manual view toggle so an old seed does
  // not silently re-apply.
  const [feedbackSessionSeed, setFeedbackSessionSeed] = React.useState<
    string | undefined
  >();

  // ---- Handlers ----
  const handleSelectAgentThread = (threadId: string) => {
    setSelectedAgentThreadId(threadId);
    setScrollToMessageSequenceNum(undefined);
    setSelectedFeedback(undefined);
  };

  const handleBack = () => {
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    setSelectedFeedback(undefined);
  };

  const handleSelectFeedback = (feedback: AgentFeedbackRecord) => {
    setSelectedAgentThreadId(feedback.thread_id);
    setScrollToMessageSequenceNum(feedback.message_sequence_num);
    setSelectedFeedback(feedback);
  };

  const handleShowFeedbackForAgentThread = (threadId: string) => {
    // Switch to the feedback view pre-filtered to this session. The seed is
    // read once when FeedbackTable mounts (which happens on this view switch).
    setFeedbackSessionSeed(threadId);
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    setViewMode("feedback");
  };

  // ---- Deep-link hydration / URL sync ----
  // URL is the source of truth for ``sessionId`` and ``feedbackId`` so admins
  // can share links to a specific thread or feedback entry. On mount we copy
  // those params into state; subsequent state changes write back to the URL.
  //
  // The writer is gated on ``hasHydrated`` so it does not fire on the very
  // first render and clobber params we are still waiting on (the feedback
  // hydration path is async — an unguarded writer would delete the
  // ``feedbackId`` we just read from the URL before the fetch resolved).
  const urlSessionId = searchParams.get("sessionId") ?? undefined;
  const urlFeedbackId = searchParams.get("feedbackId") ?? undefined;

  const [hasHydrated, setHasHydrated] = React.useState<boolean>(
    // Nothing to hydrate → we can write the URL freely from first render.
    () => !shouldHydrateFromUrl(searchParams),
  );
  const [hydrationError, setHydrationError] = React.useState<string | null>(
    null,
  );

  // Fetch the feedback record when we land on a shared link, switch to the
  // feedback view (a ``feedbackId``-only deep link implies "show me this
  // feedback" — landing in threads view would show the thread with an unused
  // selection in the parent panel), and flip ``hasHydrated`` once state
  // catches up.
  React.useEffect(() => {
    if (!urlFeedbackId) {
      return;
    }
    if (selectedFeedback?.feedback_id === urlFeedbackId) {
      setHasHydrated(true);
      return;
    }
    let cancelled = false;
    void adminService
      .getAdminFeedbackById(urlFeedbackId)
      .then((record) => {
        if (cancelled) {
          return;
        }
        setSelectedFeedback(record);
        setSelectedAgentThreadId(record.thread_id);
        setScrollToMessageSequenceNum(record.message_sequence_num);
        // Force feedback view so the triage panel actually renders; without
        // this the URL writer would later emit ``view=feedback`` once state
        // settles, but the user briefly sees the threads view first.
        setViewMode("feedback");
        setFeedbackSessionSeed(record.thread_id);
        setHasHydrated(true);
      })
      .catch((error: unknown) => {
        // Bad/stale feedbackId shouldn't freeze the UI — allow the writer to
        // proceed so the URL reflects reality, and surface what happened so
        // the admin isn't left staring at an empty triage panel.
        if (cancelled) {
          return;
        }
        setHasHydrated(true);
        const detail = error instanceof Error ? error.message : "unknown error";
        setHydrationError(
          `Couldn't load the feedback from the shared link (${detail}). It may have been deleted.`,
        );
      });
    return () => {
      cancelled = true;
    };
    // setState dispatchers are stable; intentionally excluded to avoid
    // re-running the hydration fetch.
  }, [urlFeedbackId, selectedFeedback?.feedback_id, adminService]);

  // Simpler case: only a sessionId is shared. Select the thread directly.
  // Gated on ``!hasHydrated`` so this only fires once at mount. Without the
  // gate it fought the URL writer below: a user click updated state in the
  // same render whose closure still saw the old ``urlSessionId``, so this
  // effect reverted state while the writer pushed the new URL — flipping
  // back and forth between two session ids indefinitely.
  React.useEffect(() => {
    if (hasHydrated) {
      return;
    }
    if (urlFeedbackId) {
      return; // handled by the feedback-hydration effect
    }
    if (!urlSessionId) {
      return;
    }
    if (selectedAgentThreadId !== urlSessionId) {
      setSelectedAgentThreadId(urlSessionId);
      setSelectedFeedback(undefined);
      setScrollToMessageSequenceNum(undefined);
      if (viewMode === "feedback") {
        setFeedbackSessionSeed(urlSessionId);
      }
    }
    setHasHydrated(true);
    // setState dispatchers are stable; intentionally excluded.
  }, [
    hasHydrated,
    urlFeedbackId,
    urlSessionId,
    selectedAgentThreadId,
    viewMode,
  ]);

  // Single point-of-truth writer for the URL: ``view``, ``sessionId``,
  // ``feedbackId``. Other params (``tab`` set by Admin.tsx) are preserved.
  // Copying the browser URL at any moment yields a working share link.
  React.useEffect(() => {
    if (!hasHydrated) {
      return;
    }
    const next = nextExploreUrlParams(searchParams, {
      viewMode,
      selectedSessionId: selectedFeedback?.thread_id ?? selectedAgentThreadId,
      selectedFeedbackId: selectedFeedback?.feedback_id,
    });
    if (next !== null) {
      setSearchParams(next, { replace: true });
    }
    // Intentionally exclude searchParams/setSearchParams from deps — they
    // change identity each render and would retrigger the effect in a loop.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasHydrated,
    viewMode,
    selectedAgentThreadId,
    selectedFeedback?.feedback_id,
    selectedFeedback?.thread_id,
  ]);

  // Caption on the rated message in the thread viewer. It rides in the
  // viewer's thread context, so a new element on every render here would
  // re-render the whole conversation.
  const selectedFeedbackSentiment = selectedFeedback?.sentiment;
  const scrollTargetLabel = React.useMemo(
    () =>
      selectedFeedbackSentiment === undefined ? undefined : (
        <>
          <FeedbackSentimentIcon sentiment={selectedFeedbackSentiment} />
          User feedback on this message
        </>
      ),
    [selectedFeedbackSentiment],
  );

  return (
    <div className={styles.container}>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        size="small"
        onChange={(_e, value: unknown) => {
          if (value === "agentThreads" || value === "feedback") {
            // Manual switch starts the feedback view fresh — drop any seed left
            // over from a deep link / cross-view hand-off so it doesn't silently
            // re-apply the old session filter.
            setFeedbackSessionSeed(undefined);
            setViewMode(value);
          }
        }}
        aria-label="Explore Threads view mode"
      >
        <ToggleButton value="agentThreads">Threads</ToggleButton>
        <ToggleButton value="feedback">Feedback</ToggleButton>
      </ToggleButtonGroup>

      {viewMode === "agentThreads" ? (
        <AgentThreadsTable
          selectedAgentThreadId={selectedAgentThreadId}
          onSelectAgentThread={handleSelectAgentThread}
          onOpenFeedbackForAgentThread={handleShowFeedbackForAgentThread}
          orgOptions={orgOptions}
          userOptions={userOptions}
          agentOptions={agentOptions}
        />
      ) : (
        <>
          <FeedbackTable
            orgOptions={orgOptions}
            userOptions={userOptions}
            initialSessionId={feedbackSessionSeed}
            onSelectFeedback={handleSelectFeedback}
            selectedFeedbackId={selectedFeedback?.feedback_id}
          />
          {selectedFeedback && (
            <FeedbackTriagePanel
              feedback={selectedFeedback}
              onUpdated={(updated) =>
                // A save that finishes after another row was selected must not
                // switch the panel back to the row it saved.
                setSelectedFeedback((current) =>
                  current?.feedback_id === updated.feedback_id
                    ? updated
                    : current,
                )
              }
            />
          )}
        </>
      )}

      {selectedAgentThreadId && (
        <>
          <Divider />
          <AdminAgentThreadViewer
            threadId={selectedAgentThreadId}
            onBack={handleBack}
            scrollToMessageSequenceNum={scrollToMessageSequenceNum}
            scrollTargetLabel={scrollTargetLabel}
            onViewFeedbackForAgentThread={() =>
              handleShowFeedbackForAgentThread(selectedAgentThreadId)
            }
            onAgentThreadSelected={handleSelectAgentThread}
            shareUrl={
              selectedFeedback
                ? buildAdminFeedbackLink({
                    sessionId: selectedFeedback.thread_id,
                    feedbackId: selectedFeedback.feedback_id,
                  })
                : buildAdminAgentThreadLink({
                    sessionId: selectedAgentThreadId,
                  })
            }
          />
        </>
      )}

      <Snackbar
        open={hydrationError !== null}
        autoHideDuration={6000}
        onClose={() => setHydrationError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setHydrationError(null)}
        >
          {hydrationError}
        </Alert>
      </Snackbar>
    </div>
  );
}
